import Layout from "../../components/Layout";
import { ArrowLeft } from "lucide-react";
import useScrollReveal from "../../hooks/useScrollReveal";
import useCounter from "../../hooks/useCounter";
import ProjectTOC from "../../components/ProjectTOC";
import ComparisonBlock from "../../components/ComparisonBlock";
import ProjectNav from "../../components/ProjectNav";

export default function MorexAI() {
  useScrollReveal();

  const defectReduction = useCounter(40);
  const workflows = useCounter(5);
  const qcStages = useCounter(3);

  const sections = [
    { id: "overview", label: "Overview" },
    { id: "architecture", label: "Architecture" },
    { id: "contributions", label: "Contributions" },
    { id: "solutions", label: "AI Solutions" },
    { id: "pipeline", label: "Industry 4.0 Pipeline" },
    { id: "diagram", label: "System Diagram" },
    { id: "compare", label: "Before vs After" },
    { id: "download", label: "Download" },
  ];

  return (
    <Layout>
      {/* Sticky TOC */}
      <ProjectTOC sections={sections} />

      <div className="max-w-4xl mx-auto py-20 px-6 space-y-24 font-inter">

        {/* Back */}
        <a
          href="/projects"
          className="inline-flex items-center gap-2 text-sm opacity-60 hover:opacity-100 transition"
        >
          <ArrowLeft size={16} />
          Back to Projects
        </a>

        {/* Hero */}
        <header className="space-y-6 reveal">
          <h1 className="text-5xl md:text-6xl font-semibold tracking-tight leading-[1.1]">
            AI & Automation Strategy <br /> for MOREX Manufacturing
          </h1>

          <p className="text-lg md:text-xl opacity-70 max-w-2xl">
            A complete Industry 4.0 transformation blueprint for a{" "}
            <span className="highlight-word">35-worker automotive components factory</span>,
            introducing AI-vision QC, predictive maintenance, workflow digitalisation and
            inventory intelligence.
          </p>

          <p className="text-sm uppercase tracking-[0.25em] opacity-50">
            Industrial AI • Process Automation • Predictive Maintenance
          </p>
        </header>

        {/* Metrics */}
        <section className="grid md:grid-cols-3 gap-10 reveal">
          {[
            { label: "Mapped Workflows", value: `${workflows}+` },
            { label: "QC Stages Analysed", value: `${qcStages}-Stage` },
            { label: "Defect Reduction Potential", value: `${defectReduction}%` },
          ].map((item) => (
            <div
              key={item.label}
              className="metric rounded-3xl border border-black/10 dark:border-white/10 
                         bg-white/50 dark:bg-white/5 backdrop-blur-xl p-10 text-center shadow-sm"
            >
              <p className="text-5xl md:text-6xl font-semibold mb-3 tracking-tight">{item.value}</p>
              <p className="text-sm uppercase tracking-wide opacity-70">{item.label}</p>
            </div>
          ))}
        </section>

        <div id="overview" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        {/* OVERVIEW */}
        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Overview</h2>

          <p className="text-lg opacity-80 leading-relaxed">
            MOREX manufactures automotive metal parts for India’s domestic supply chain.
            The existing setup relies on{" "}
            <span className="highlight-word">manual QC checks</span>,
            <span className="highlight-word"> memory-based inventory estimates</span>,
            and <span className="highlight-word">non-digitised workflow boards</span>.
            These introduce operator dependency, human error and inconsistent product quality.
          </p>

          <p className="text-lg opacity-80 leading-relaxed">
            This project delivers a full{" "}
            <span className="highlight-word">AI automation roadmap</span>,
            bringing the factory to an Industry 4.0-aligned system with 
            real-time QC, predictive maintenance, digital workflows, and demand forecasting.
          </p>
        </section>

        {/* ARCHITECTURE */}
        <div id="architecture" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="space-y-8 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Technical Architecture</h2>

          <ul className="list-disc ml-6 space-y-4 text-lg opacity-80 leading-relaxed">
            <li>
              <span className="highlight-word font-semibold">AI-Vision QC System:</span>{" "}
              Camera-based defect detection trained on product images, replacing human visual checks.
            </li>
            <li>
              <span className="highlight-word font-semibold">Predictive Maintenance Sensors:</span>{" "}
              Vibration, RPM and temperature sensors feeding ML anomaly detectors.
            </li>
            <li>
              <span className="highlight-word font-semibold">Digital Workflow Engine:</span>{" "}
              Replaces whiteboards with React dashboards for task assignment and tracking.
            </li>
            <li>
              <span className="highlight-word font-semibold">Inventory Forecasting:</span>{" "}
              Time-series forecasting stabilising raw material variability.
            </li>
            <li>
              <span className="highlight-word font-semibold">AR/VR Training System:</span>{" "}
              Simulation-driven upskilling for machine operators + safety drills.
            </li>
          </ul>
        </section>

        {/* CONTRIBUTIONS */}
        <div id="contributions" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="rounded-3xl p-10 bg-white/30 dark:bg-white/5 
                            backdrop-blur-xl border border-black/10 dark:border-white/10 
                            space-y-8 reveal">
          <h3 className="text-3xl font-semibold">My Technical Contributions</h3>

          <ul className="list-disc ml-6 space-y-3 text-lg opacity-80 leading-relaxed">
            <li>Performed end-to-end factory workflow mapping</li>
            <li>Designed AI-Vision QC architecture + camera positioning</li>
            <li>Defined predictive maintenance sensor stack</li>
            <li>Built digital workflow & task queue system design</li>
            <li>Created forecasting algorithms for raw-material flow</li>
            <li>Drafted the validated Industry 4.0 transformation roadmap</li>
          </ul>
        </section>

        {/* SOLUTIONS */}
        <div id="solutions" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="space-y-8 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">AI & Automation Solutions</h2>

          <div className="space-y-6 text-lg opacity-80 leading-relaxed">
            <div>
              <h3 className="text-2xl font-semibold">AI-Vision Quality Control</h3>
              <p className="opacity-70">
                Identifies surface defects, dimensional irregularities and scratches automatically.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-semibold">Predictive Maintenance</h3>
              <p className="opacity-70">
                Uses vibration signatures + temperature drift to predict bearing failures
                and lathe misalignment before downtime.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-semibold">Inventory Forecasting</h3>
              <p className="opacity-70">
                Prevents shortages/overstock using demand prediction models.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-semibold">Digital Workflow System</h3>
              <p className="opacity-70">
                Centralised task allocation with live progress tracking.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-semibold">AR/VR Worker Training</h3>
              <p className="opacity-70">
                Builds muscle memory for equipment handling and emergency response.
              </p>
            </div>
          </div>
        </section>

        {/* PIPELINE */}
        <div id="pipeline" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Industry 4.0 Pipeline</h2>

          <ul className="list-decimal ml-6 space-y-4 text-lg opacity-80 leading-relaxed">
            <li>Factory assessment & workflow mapping</li>
            <li>Pilot phase: AI-QC + digital workflows on one cell</li>
            <li>Scaling predictive maintenance factory-wide</li>
            <li>Rolling out inventory forecasting</li>
            <li>AR/VR training deployment</li>
          </ul>
        </section>

        {/* SYSTEM DIAGRAM */}
        <div id="diagram" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">System Diagram</h2>

          <div
            className="p-6 rounded-3xl bg-white/20 dark:bg-white/5 
                       backdrop-blur-xl border border-black/10 
                       dark:border-white/10 font-mono text-sm 
                       whitespace-pre overflow-x-auto reveal"
          >
{`
AI-VISION QC → PREDICTIVE MAINTENANCE → INVENTORY FORECASTING → DIGITAL WORKFLOWS → AR/VR TRAINING

[Camera Input] → [Sensors] → [Time-Series Model] → [Task Queue] → [Training Engine]
`}
          </div>
        </section>

        {/* BEFORE / AFTER */}
        <div id="compare" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="space-y-6 reveal">
          <h2 className="text-4xl font-semibold tracking-tight">Before vs After Automation</h2>

          <ComparisonBlock
            aTitle="After Automation"
            bTitle="Before Automation"
            aContent={
              <p className="text-lg opacity-80">
                • AI-driven QC accuracy<br />
                • Predictive maintenance reduces downtime<br />
                • Digitalised tasks reduce human dependency<br />
                • Smooth supply chain via forecasting
              </p>
            }
            bContent={
              <p className="text-lg opacity-80">
                • Manual visual QC<br />
                • Unplanned machine failures<br />
                • Whiteboard task assignments<br />
                • Raw material shortages common
              </p>
            }
          />
        </section>

        {/* DOWNLOAD */}
        <div id="download" className="w-full h-px bg-black/10 dark:bg-white/10 divider-animated" />

        <section className="reveal">
          <a
            href="/morex-automation.pdf"
            className="text-lg underline opacity-70 hover:opacity-100 transition"
            download
          >
            Download full research (PDF)
          </a>
        </section>

        {/* NAVIGATION */}
        <ProjectNav
          prev={{ href: "/projects/ai-education", label: "← AI for Education" }}
          next={{ href: "/projects/i3fy", label: "I3FY Platform" }}
        />
      </div>
    </Layout>
  );
}
